import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { toast } from 'react-toastify';

export interface ISalesStatistics {
  companyName: string;
  createdAt: string;
  amount: {
    ORDER_COUNT: number;
    ORDER_AMOUNT: number;
  };
}

export interface IFranchiseSummaryItem {
  franchiseId: string;
  companyName: string;
  totalOrderCount: number;
  confirmedCount: number;
  totalOrderAmount: number;
  averageOrderAmount: number;
}

export interface IDailySalesTotalSummary {
  totalOrderCount: number;
  totalOrderAmount: number;
  totalFranchiseCount: number;
}

interface SalesStatisticsResponse {
  page: {
    content: ISalesStatistics[];
    totalElements: number;
    totalPages: number;
  };
  summary: IFranchiseSummaryItem[];
}

// 일자별 요약 (date 파라미터 없을 때)
export interface IDailySalesSummary {
  companyName: string;
  createdAt: string;
  amount: {
    FRANCHISE_COUNT: number;
    ORDER_COUNT: number;
    ORDER_AMOUNT: number;
  };
}

// 일자별 상세 (date 파라미터 있을 때)
export interface IDailySalesDetail {
  franchiseId: string;
  companyName: string;
  createdAt: string;
  amount: {
    ORDER_COUNT: number;
    ORDER_AMOUNT: number;
  };
}

interface DailySalesResponse {
  page: {
    content: IDailySalesSummary[];
    totalElements: number;
    totalPages: number;
    number: number;
  };
  summary: IDailySalesTotalSummary;
}

interface DailySalesDetailResponse {
  page: {
    content: IDailySalesDetail[];
    totalElements: number;
    totalPages: number;
  };
  summary: any;
}

type SalesStatisticsData = {
  getSalesStatisticsLoading: boolean;
  salesStatistics: ISalesStatistics[];
  franchiseSummary: IFranchiseSummaryItem[];
  dailySalesLoading: boolean;
  dailySales: IDailySalesSummary[];
  dailySalesPage: { totalElements: number; totalPages: number; number: number } | null;
  dailySalesTotalSummary: IDailySalesTotalSummary | null;
  dailySalesDetails: Record<string, IDailySalesDetail[]>;
  dailySalesDetailLoading: Record<string, boolean>;
};

type SalesStatisticsAction = {
  getSalesStatistics: (params: {
    startDt: string;
    endDt: string;
    franchiseId?: string;
  }) => void;
  getDailySales: (params: {
    startDt: string;
    endDt: string;
    franchiseId?: string;
    page?: number;
    size?: number;
  }) => void;
  getDailySalesDetail: (params: {
    startDt: string;
    endDt: string;
    date: string;
    franchiseId?: string;
  }) => void;
};

export const useSalesStatisticsStore = create<
  SalesStatisticsData & SalesStatisticsAction
>((set, get) => ({
  getSalesStatisticsLoading: false,
  salesStatistics: [],
  franchiseSummary: [],
  dailySalesLoading: false,
  dailySales: [],
  dailySalesPage: null,
  dailySalesTotalSummary: null,
  dailySalesDetails: {},
  dailySalesDetailLoading: {},

  getSalesStatistics: async ({ startDt, endDt, franchiseId }) => {
    set({ getSalesStatisticsLoading: true });

    try {
      const response = await apiClient.get<SalesStatisticsResponse>(
        '/contract-statistics/franchise',
        {
          params: {
            startDt,
            endDt,
            franchiseId: franchiseId || undefined,
            page: 0,
            size: 100,
          },
        }
      );

      set({
        salesStatistics: response.data.page.content || [],
        franchiseSummary: response.data.summary || [],
      });
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('판매 통계를 가져오지 못했습니다.');
    }

    set({ getSalesStatisticsLoading: false });
  },

  getDailySales: async ({ startDt, endDt, franchiseId, page = 0, size = 20 }) => {
    set({ dailySalesLoading: true, dailySalesDetails: {}, dailySalesDetailLoading: {} });

    try {
      const response = await apiClient.get<DailySalesResponse>(
        '/contract-statistics/franchise/daily',
        {
          params: {
            startDt,
            endDt,
            franchiseId: franchiseId || undefined,
            page,
            size,
          },
        }
      );

      set({
        dailySales: response.data.page.content || [],
        dailySalesPage: {
          totalElements: response.data.page.totalElements,
          totalPages: response.data.page.totalPages,
          number: response.data.page.number,
        },
        dailySalesTotalSummary: response.data.summary || null,
      });
    } catch (error) {
      console.error('일자별 판매 통계 API 요청 실패:', error);
      toast.error('일자별 판매 통계를 가져오지 못했습니다.');
    }

    set({ dailySalesLoading: false });
  },

  getDailySalesDetail: async ({ startDt, endDt, date, franchiseId }) => {
    const { dailySalesDetails, dailySalesDetailLoading } = get();

    // 이미 캐시된 데이터가 있으면 스킵
    if (dailySalesDetails[date]) return;

    set({
      dailySalesDetailLoading: { ...dailySalesDetailLoading, [date]: true },
    });

    try {
      const response = await apiClient.get<DailySalesDetailResponse>(
        '/contract-statistics/franchise/daily',
        {
          params: {
            startDt,
            endDt,
            date,
            franchiseId: franchiseId || undefined,
            page: 0,
            size: 1000,
          },
        }
      );

      const { dailySalesDetails: currentDetails, dailySalesDetailLoading: currentLoading } = get();
      set({
        dailySalesDetails: {
          ...currentDetails,
          [date]: response.data.page.content || [],
        },
        dailySalesDetailLoading: {
          ...currentLoading,
          [date]: false,
        },
      });
    } catch (error) {
      console.error('일자별 상세 통계 API 요청 실패:', error);
      toast.error('일자별 상세 통계를 가져오지 못했습니다.');

      const { dailySalesDetailLoading: currentLoading } = get();
      set({
        dailySalesDetailLoading: {
          ...currentLoading,
          [date]: false,
        },
      });
    }
  },
}));
